import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

export default api;

// Content
export const getSettings = () => api.get("/settings").then((r) => r.data);
export const getStats = () => api.get("/stats").then((r) => r.data);
export const getServices = () => api.get("/services").then((r) => r.data);
export const getService = (slug) => api.get(`/services/${slug}`).then((r) => r.data);
export const getCourses = (params) => api.get("/courses", { params }).then((r) => r.data);
export const getCourse = (id) => api.get(`/courses/${id}`).then((r) => r.data);
export const getProjects = () => api.get("/projects").then((r) => r.data);
export const getProject = (id) => api.get(`/projects/${id}`).then((r) => r.data);
export const getCaseStudies = () => api.get("/case-studies").then((r) => r.data);
export const getCaseStudy = (id) => api.get(`/case-studies/${id}`).then((r) => r.data);
export const getClients = () => api.get("/clients").then((r) => r.data);
export const getTestimonials = () => api.get("/testimonials").then((r) => r.data);
export const getResources = () => api.get("/resources").then((r) => r.data);
export const getProducts = (params) => api.get("/products", { params }).then((r) => r.data);
export const getProduct = (id) => api.get(`/products/${id}`).then((r) => r.data);
export const getBlog = (params) => api.get("/blog", { params }).then((r) => r.data);
export const getBlogPost = (slug) => api.get(`/blog/${slug}`).then((r) => r.data);
export const getFaqs = () => api.get("/faqs").then((r) => r.data);
export const getWriting = (params) => api.get("/writing", { params }).then((r) => r.data);
export const getWritingPost = (slug) => api.get(`/writing/${slug}`).then((r) => r.data);
export const getAds = (placement) => api.get("/ads", { params: { placement } }).then((r) => r.data);

// Actions
export const subscribeNewsletter = (email, source) => api.post("/newsletter", { email, source }).then((r) => r.data);
export const sendContact = (payload) => api.post("/contact", payload).then((r) => r.data);
export const downloadResource = (id, email) => api.post(`/resources/${id}/download`, { email }).then((r) => r.data);
export const trackPageview = (path, referrer) => {
  const key = "jm_sid";
  let sid = "";
  try {
    sid = sessionStorage.getItem(key) || "";
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(key, sid);
    }
  } catch { /* ignore */ }
  const utm = {};
  try {
    const q = new URLSearchParams(window.location.search);
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content"]) {
      const v = q.get(k);
      if (v) utm[k] = v.slice(0, 80);
    }
  } catch { /* ignore */ }
  return api.post("/analytics/pageview", {
    path,
    referrer: referrer || (typeof document !== "undefined" ? document.referrer : ""),
    session_id: sid,
    ...utm,
  }).then((r) => r?.data?.id || null).catch(() => null);
};

export const trackPageviewLeave = (id, durationMs) => {
  if (!id || !durationMs) return;
  try {
    // Beacon is the most reliable way to send on unload; falls back to fetch.
    const url = `${API}/analytics/pageview/leave`;
    const body = JSON.stringify({ id, duration_ms: Math.round(durationMs) });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true });
    }
  } catch { /* ignore */ }
};
export const adImpression = (id) => api.post(`/ads/${id}/impression`).catch(() => {});
export const adClick = (id) => api.post(`/ads/${id}/click`).catch(() => {});

// Payments
export const checkoutProducts = (items, origin_url, email) =>
  api.post("/payments/checkout", { items, origin_url, email }).then((r) => r.data);
export const enrollCourse = (course_id, origin_url, email) =>
  api.post("/payments/enroll", { course_id, origin_url, email }).then((r) => r.data);
export const paymentStatus = (sid) => api.get(`/payments/status/${sid}`).then((r) => r.data);

// Auth / admin
export const login = (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);
export const getMe = () => api.get("/auth/me").then((r) => r.data);
export const adminAnalytics = () => api.get("/admin/analytics").then((r) => r.data);
export const adminAnalyticsDetailed = (days = 30) => api.get(`/admin/analytics/detailed`, { params: { days } }).then((r) => r.data);
export const adminContacts = () => api.get("/admin/contacts").then((r) => r.data);
export const adminSubscribers = () => api.get("/admin/subscribers").then((r) => r.data);
export const adminList = (c) => api.get(`/admin/${c}`).then((r) => r.data);
export const adminCreate = (c, body) => api.post(`/admin/${c}`, body).then((r) => r.data);
export const adminUpdate = (c, id, body) => api.put(`/admin/${c}/${id}`, body).then((r) => r.data);
export const adminDelete = (c, id) => api.delete(`/admin/${c}/${id}`).then((r) => r.data);
export const adminGetSettings = () => api.get("/admin/settings").then((r) => r.data);
export const adminUpdateSettings = (body) => api.put("/admin/settings", body).then((r) => r.data);
export const socialStatus = () => api.get("/admin/social/status").then((r) => r.data);
export const kaiAnnounce = () => api.post("/admin/kai/announce").then((r) => r.data);
export const socialToggle = (enabled) => api.post("/admin/social/toggle", { enabled }).then((r) => r.data);
export const socialConnectLinkedIn = (access_token) => api.post("/admin/social/linkedin/connect", { access_token }).then((r) => r.data);
export const socialConnectFacebook = (payload) => api.post("/admin/social/facebook/connect", payload).then((r) => r.data);
export const socialSelectFacebookPage = (page_id) => api.post("/admin/social/facebook/select", { page_id }).then((r) => r.data);
export const socialBackfill = (per_day = 4) => api.post("/admin/social/backfill", null, { params: { per_day } }).then((r) => r.data);
export const socialDisconnect = (provider) => api.delete(`/admin/social/${provider}`).then((r) => r.data);
export const socialClicks = (days = 30) => api.get("/admin/social/clicks", { params: { days } }).then((r) => r.data);
export const socialPinterestSaveApp = (payload) => api.post("/admin/social/pinterest/app", payload).then((r) => r.data);
export const socialPinterestOAuthUrl = () => api.get("/admin/social/pinterest/oauth-url").then((r) => r.data);
export const socialPinterestBoards = () => api.get("/admin/social/pinterest/boards").then((r) => r.data);
export const socialPinterestSelect = (board_id, board_name) => api.post("/admin/social/pinterest/select", { board_id, board_name }).then((r) => r.data);
export const socialInstagramDetect = () => api.post("/admin/social/instagram/detect").then((r) => r.data);
export const socialTikTokSaveApp = (payload) => api.post("/admin/social/tiktok/app", payload).then((r) => r.data);
export const socialTikTokOAuthUrl = () => api.get("/admin/social/tiktok/oauth-url").then((r) => r.data);
export const avenoEngineSync = () => api.post("/admin/aveno/engine/sync").then((r) => r.data);
export const avenoEngineStatus = () => api.get("/admin/aveno/engine/status").then((r) => r.data);
export const avenoEngineToggle = (enabled, per_day = 7) => api.post("/admin/aveno/engine/toggle", { enabled, per_day }).then((r) => r.data);
export const avenoEngineRunNext = () => api.post("/admin/aveno/engine/run-next").then((r) => r.data);
export const avenoEngineRefreshAll = (per_day = 7, regen_videos = false) => api.post("/admin/aveno/engine/refresh-all", null, { params: { per_day, regen_videos } }).then((r) => r.data);
export const socialFacebookPosts = (after) => api.get("/admin/social/facebook/posts", { params: after ? { after } : {} }).then((r) => r.data);
export const socialFacebookDeletePost = (post_id, slug) => api.post("/admin/social/facebook/posts/delete", { post_id, slug }).then((r) => r.data);
export const socialFacebookDeleteAveno = (post_ids) => api.post("/admin/social/facebook/posts/delete-aveno", { post_ids }).then((r) => r.data);
export const socialFacebookFixLinks = () => api.post("/admin/social/facebook/posts/fix-links").then((r) => r.data);
export const socialPreview = (slug) => api.get(`/admin/social/preview/${slug}`).then((r) => r.data);
export const listPublicBlog = (limit = 100) => api.get("/blog", { params: { limit } }).then((r) => r.data);
export const socialSetVoice = (voice) => api.post("/admin/social/voice", { voice }).then((r) => r.data);
export const socialVoiceSample = (voice) => api.get("/admin/social/voice-sample", { params: { voice }, responseType: "blob" }).then((r) => r.data);
export const socialRequeueErrors = () => api.post("/admin/social/requeue-errors").then((r) => r.data);
export const socialFacebookRequeueFailed = () => api.post("/admin/social/facebook/requeue-failed").then((r) => r.data);
export const socialNetworksList = () => api.get("/admin/social/networks").then((r) => r.data);
export const socialNetworkSave = (network, values) => api.post(`/admin/social/networks/${network}`, values).then((r) => r.data);
export const socialNetworkDelete = (network) => api.delete(`/admin/social/networks/${network}`).then((r) => r.data);
export const floorPlannerSave = (payload) => api.post("/floor-planner/projects", payload).then((r) => r.data);
export const floorPlannerGet = (code) => api.get(`/floor-planner/projects/${code}`).then((r) => r.data);
export const floorPlannerLead = (payload) => api.post("/floor-planner/leads", payload).then((r) => r.data);
export const educationStatus = () => api.get("/admin/education/status").then((r) => r.data);
export const educationToggle = (patch) => api.post("/admin/education/toggle", patch).then((r) => r.data);
export const educationGenerate = (topic = "", social = true) => api.post("/admin/education/generate", { topic, social }).then((r) => r.data);
export const educationSeed = () => api.post("/admin/education/seed").then((r) => r.data);
export const toolsEngineStatus = () => api.get("/admin/tools-engine/status").then((r) => r.data);
export const toolsEngineToggle = (patch) => api.post("/admin/tools-engine/toggle", patch).then((r) => r.data);
export const toolsEngineRunNow = () => api.post("/admin/tools-engine/run-now").then((r) => r.data);
export const toolBuilderStatus = () => api.get("/admin/tool-builder/status").then((r) => r.data);
export const toolBuilderToggle = (patch) => api.post("/admin/tool-builder/toggle", patch).then((r) => r.data);
export const toolBuilderRunNow = () => api.post("/admin/tool-builder/run-now").then((r) => r.data);
export const skillEngineStatus = () => api.get("/admin/skill-engine/status").then((r) => r.data);
export const skillEngineToggle = (patch) => api.post("/admin/skill-engine/toggle", patch).then((r) => r.data);
export const skillEngineRunNow = () => api.post("/admin/skill-engine/run-now").then((r) => r.data);
export const funEngineStatus = () => api.get("/admin/fun-engine/status").then((r) => r.data);
export const funEngineToggle = (patch) => api.post("/admin/fun-engine/toggle", patch).then((r) => r.data);
export const funEngineRunNow = (kind) => api.post("/admin/fun-engine/run-now", { kind }).then((r) => r.data);
export const funEngineBoostAll = () => api.post("/admin/fun-engine/boost", { all: true }).then((r) => r.data);
export const fbRepliesStatus = () => api.get("/admin/fb-replies/status").then((r) => r.data);
export const fbRepliesToggle = (patch) => api.post("/admin/fb-replies/toggle", patch).then((r) => r.data);
export const getToolsCatalog = () => api.get("/tools/catalog").then((r) => r.data);
export const getToolSpec = (slug) => api.get(`/tools/spec/${slug}`).then((r) => r.data);
export const getAvenoVideos = (handle) => api.get(`/aveno-videos/${handle}`).then((r) => r.data).catch(() => ({ vertical: null, landscape: null }));

// Admin tools
export const aiGenerateBlog = (topic, category, tone, include_affiliates = true) =>
  api.post("/admin/blog/generate", { topic, category, tone, include_affiliates }).then((r) => r.data);
export const getIntegrations = () => api.get("/admin/integrations").then((r) => r.data);
export const updateIntegrations = (body) => api.put("/admin/integrations", body).then((r) => r.data);

// Auto blog
export const getAutoBlogConfig = () => api.get("/admin/blog/auto-config").then((r) => r.data);
export const updateAutoBlogConfig = (body) => api.put("/admin/blog/auto-config", body).then((r) => r.data);
export const autoBlogRunNow = () => api.post("/admin/blog/auto-run-now").then((r) => r.data);

// Tools Pro entitlement (persistent across devices)
export const toolsProVerify = (email) => api.post("/tools/pro/verify", { email }).then((r) => r.data);
export const toolsProFromSession = (sessionId) => api.get(`/tools/pro/session/${sessionId}`).then((r) => r.data);

// My Downloads
export const getDownloads = (email) => api.get(`/downloads`, { params: { email } }).then((r) => r.data);
export const getDelivery = (token) => api.get(`/delivery/${token}`).then((r) => r.data);

// Reviews (public)
export const getReviews = (productId) => api.get(`/products/${productId}/reviews`).then((r) => r.data);
export const canReview = (productId, email) => api.get(`/products/${productId}/can-review`, { params: { email } }).then((r) => r.data);
export const createReview = (productId, payload) => api.post(`/products/${productId}/reviews`, payload).then((r) => r.data);

// Free enrollments (Mason Academy)
export const enrollFree = (courseId, email) => api.post("/enrollments/enroll", { email, course_id: courseId }).then((r) => r.data);
export const getMyEnrollments = (email) => api.get(`/enrollments`, { params: { email } }).then((r) => r.data);
export const getEnrollment = (courseId, email) => api.get(`/enrollments/${courseId}`, { params: { email } }).then((r) => r.data);
export const completeLesson = (courseId, lessonId, email) => api.post("/enrollments/complete-lesson", { course_id: courseId, lesson_id: lessonId, email }).then((r) => r.data);

// Certificates
export const issueCertificate = (payload) => api.post("/certificates", payload).then((r) => r.data);
export const getCertificate = (certId) => api.get(`/certificates/${certId}`).then((r) => r.data);
export const listMyCertificates = (email) => api.get(`/certificates`, { params: { email } }).then((r) => r.data);

// Forum
export const listThreads = (courseId) => api.get(`/courses/${courseId}/threads`).then((r) => r.data);
export const createThread = (courseId, payload) => api.post(`/courses/${courseId}/threads`, payload).then((r) => r.data);
export const getThread = (threadId) => api.get(`/threads/${threadId}`).then((r) => r.data);
export const createReply = (threadId, payload) => api.post(`/threads/${threadId}/replies`, payload).then((r) => r.data);
export const upvoteThread = (threadId, email) => api.post(`/threads/${threadId}/upvote`, null, { params: { email } }).then((r) => r.data);
export const upvoteReply = (replyId, email) => api.post(`/replies/${replyId}/upvote`, null, { params: { email } }).then((r) => r.data);

// Admin — forum
export const adminListForum = () => api.get(`/admin/forum`).then((r) => r.data);
export const adminUpdateThread = (id, body) => api.patch(`/admin/threads/${id}`, body).then((r) => r.data);
export const adminDeleteThread = (id) => api.delete(`/admin/threads/${id}`).then((r) => r.data);
export const adminUpdateReply = (id, body) => api.patch(`/admin/replies/${id}`, body).then((r) => r.data);
export const adminDeleteReply = (id) => api.delete(`/admin/replies/${id}`).then((r) => r.data);

// Reviews moderation (admin)
export const adminListReviews = () => api.get(`/admin/reviews`).then((r) => r.data);
export const adminUpdateReview = (id, body) => api.patch(`/admin/reviews/${id}`, body).then((r) => r.data);
export const adminDeleteReview = (id) => api.delete(`/admin/reviews/${id}`).then((r) => r.data);

// Language course (English / French) progress sync
export const getLangProgress = (email, course_id = "english-for-french-speakers") =>
  api.get(`/lang/progress`, { params: { email, course_id } }).then((r) => r.data);
export const saveLangProgress = (payload) =>
  api.post(`/lang/progress`, payload).then((r) => r.data);
export const getLangLeaderboard = (course_id = "english-for-french-speakers", limit = 10) =>
  api.get(`/lang/leaderboard`, { params: { course_id, limit } }).then((r) => r.data);


// Business engine — pricing, currencies, estimates, leads, projects, care
export const getPricingConfig = () => api.get("/pricing/config").then((r) => r.data);
export const getCurrencies = () => api.get("/currencies").then((r) => r.data);
export const quoteEstimate = (payload) => api.post("/quote/estimate", payload).then((r) => r.data);
export const trackQuoteEmail = (email, estimate_id) => api.post("/quote/track", { email, estimate_id }).then((r) => r.data);
export const submitAudit = (payload) => api.post("/audit", payload).then((r) => r.data);
export const submitLead = (payload) => api.post("/leads", payload).then((r) => r.data);
export const startProject = (payload) => api.post("/projects/start", payload).then((r) => r.data);
export const signAgreement = (payload) => api.post("/agreement/sign", payload).then((r) => r.data);
export const getAgreementStatus = (sessionId) => api.get(`/agreement/status/${sessionId}`).then((r) => r.data);
export const getProjectStatus = (sid) => api.get(`/projects/status/${sid}`).then((r) => r.data);
export const careSubscribe = (payload) => api.post("/care/subscribe", payload).then((r) => r.data);

// Admin — leads pipeline + pricing config
export const adminLeads = (status) => api.get("/admin/leads", { params: status ? { status } : {} }).then((r) => r.data);
export const adminUpdateLead = (id, body) => api.patch(`/admin/leads/${id}`, body).then((r) => r.data);
export const adminDeleteLead = (id) => api.delete(`/admin/leads/${id}`).then((r) => r.data);
export const adminQuoteSessions = () => api.get("/admin/quote-sessions").then((r) => r.data);
export const adminGetPricing = () => api.get("/admin/pricing-config").then((r) => r.data);
export const adminUpdatePricing = (body) => api.put("/admin/pricing-config", body).then((r) => r.data);
export const requestPlaybook = (payload) => api.post("/playbook", payload).then((r) => r.data);
export const adminRevenueEstimate = (params = {}) => api.get("/admin/revenue-estimate", { params }).then((r) => r.data);

